/* eslint-disable jira/filename-case */
// This doesn't use @jira/design-system__navigation-system as it results in a hard-to-debug cycle.
// We need to move away from using these variables directly as it results in a brittle hard to evolve system.
// eslint-disable-next-line jira/restricted/@atlaskit+page-layout
import {
	LEFT_PANEL_WIDTH as UNSAFE_DO_NOT_USE_LEFT_PANEL_WIDTH,
	LEFT_SIDEBAR_WIDTH as UNSAFE_DO_NOT_USE_LEFT_SIDEBAR_WIDTH,
	RIGHT_PANEL_WIDTH as UNSAFE_DO_NOT_USE_RIGHT_PANEL_WIDTH,
	RIGHT_SIDEBAR_WIDTH as UNSAFE_DO_NOT_USE_RIGHT_SIDEBAR_WIDTH,
} from '@atlaskit/navigation-system/legacy/css-variables';
import { token } from '@atlaskit/tokens';
import { PAGE_LAYOUT_OFFSET_TOP } from '@jira/platform__common-constants/src/page-layout.tsx';
import type {
	ProjectThemeTokens,
	PartialProjectThemeTokens,
	ImageBackgroundConfig,
	Token,
	AdaptiveGradientBackgroundConfig,
	AdaptiveColorBackgroundConfig,
	ColorThemeName,
	GradientThemeName,
	BaseThemeTokens,
} from './types.tsx';

export const STAR_ICON_COLOR = '#FFAB00';

export const Variables: Record<Token, string> = {
	BACKDROP_FILTER: '--project-backdrop-filter', // Delete when business projects are migrated to the new theming
	COLOR_BACKGROUND_NEUTRAL_SUBTLE_HOVERED_CONTRAST:
		'--project-color-background-neutral-subtle-hovered-contrast',
	COLOR_BACKGROUND_ACCENT_SELECTED: '--project-color-background-accent-selected',
	COLOR_BACKGROUND_BRAND_BOLD: '--project-color-background-brand-bold',
	COLOR_BACKGROUND_BRAND_BOLD_HOVERED: '--project-color-background-brand-bold-hovered',
	COLOR_BACKGROUND_BRAND_BOLD_PRESSED: '--project-color-background-brand-bold-pressed',
	COLOR_BACKGROUND_DISABLED: '--project-color-background-disabled',
	COLOR_BACKGROUND_INPUT: '--project-background-input',
	COLOR_BACKGROUND_NEUTRAL: '--project-background-neutral',
	COLOR_BACKGROUND_NEUTRAL_HOVERED: '--project-background-neutral-hovered',
	COLOR_BACKGROUND_NEUTRAL_PRESSED: '--project-background-neutral-pressed',
	COLOR_BACKGROUND_NEUTRAL_SUBTLE_HOVERED: '--project-background-neutral-subtle-hovered',
	COLOR_BACKGROUND_NEUTRAL_SUBTLE_PRESSED: '--project-background-neutral-subtle-pressed',
	COLOR_BACKGROUND_SELECTED: '--project-background-selected',
	COLOR_BORDER: '--project-color-border',
	COLOR_BORDER_INPUT: '--project-color-border-input',
	COLOR_BORDER_INVERSE: '--project-color-border-inverse',
	COLOR_BORDER_SELECTED: '--project-color-border-selected',
	COLOR_ELEVATION_SURFACE: '--project-color-elevation-surface',
	COLOR_ELEVATION_SURFACE_SUNKEN: '--project-color-elevation-surface-sunken',
	COLOR_ICON_DANGER: '--project-color-icon-danger',
	COLOR_ICON_INFORMATION: '--project-color-icon-information',
	COLOR_ICON_SUCCESS: '--project-color-icon-success',
	COLOR_ICON_WARNING: '--project-color-icon-warning',
	COLOR_LINK: '--project-color-link',
	COLOR_LINK_PRESSED: '--project-color-link-pressed',
	COLOR_SKELETON: '--project-skeleton',
	COLOR_TEXT: '--project-color-text',
	COLOR_TEXT_ACCENT_SELECTED: '--project-text-accent-selected',
	COLOR_TEXT_DISABLED: '--project-color-text-disabled',
	COLOR_TEXT_INVERSE: '--project-color-text-inverse',
	COLOR_TEXT_SELECTED: '--project-color-text-selected',
	COLOR_TEXT_SUBTLE: '--project-color-text-subtle',
	COLOR_TEXT_SUBTLEST: '--project-color-text-subtlest',
	CONTENT_HEIGHT: '--project-content-height',
	CONTENT_WIDTH: '--project-content-width',
	ELEVATION_SURFACE: '--project-elevation-surface',
	ELEVATION_SURFACE_BLEND_MODE: '--project-elevation-surface-blend-mode',
	ELEVATION_SURFACE_CONTRAST: '--project-elevation-surface-contrast',
	ELEVATION_SURFACE_CONTRAST_BOLDER: '--project-elevation-surface-contrast-bolder',
	ELEVATION_SURFACE_SWIMLANE: '--project-elevation-surface-swimlane',
	WRAPPER_BACKGROUND: '--project-wrapper-background',
	WRAPPER_BORDER_RADIUS: '--project-wrapper-border-radius',
	WRAPPER_HEIGHT: '--project-wrapper-height',
	WRAPPER_PADDING: '--project-wrapper-padding',
	WRAPPER_WIDTH: '--project-wrapper-width',
};

export const Tokens: Record<Token, string> = {
	BACKDROP_FILTER: 'var(--project-backdrop-filter)',
	COLOR_BACKGROUND_NEUTRAL_SUBTLE_HOVERED_CONTRAST:
		'var(--project-color-background-neutral-subtle-hovered-contrast)',
	COLOR_BACKGROUND_ACCENT_SELECTED: 'var(--project-color-background-accent-selected)',
	COLOR_BACKGROUND_BRAND_BOLD: 'var(--project-color-background-brand-bold)',
	COLOR_BACKGROUND_BRAND_BOLD_HOVERED: 'var(--project-color-background-brand-bold-hovered)',
	COLOR_BACKGROUND_BRAND_BOLD_PRESSED: 'var(--project-color-background-brand-bold-pressed)',
	COLOR_BACKGROUND_DISABLED: 'var(--project-color-background-disabled)',
	COLOR_BACKGROUND_INPUT: 'var(--project-background-input)',
	COLOR_BACKGROUND_NEUTRAL: 'var(--project-background-neutral)',
	COLOR_BACKGROUND_NEUTRAL_HOVERED: 'var(--project-background-neutral-hovered)',
	COLOR_BACKGROUND_NEUTRAL_PRESSED: 'var(--project-background-neutral-pressed)',
	COLOR_BACKGROUND_NEUTRAL_SUBTLE_HOVERED: 'var(--project-background-neutral-subtle-hovered)',
	COLOR_BACKGROUND_NEUTRAL_SUBTLE_PRESSED: 'var(--project-background-neutral-subtle-pressed)',
	COLOR_BACKGROUND_SELECTED: 'var(--project-background-selected)',
	COLOR_BORDER: 'var(--project-color-border)',
	COLOR_BORDER_INPUT: 'var(--project-color-border-input)',
	COLOR_BORDER_INVERSE: 'var(--project-color-border-inverse)',
	COLOR_BORDER_SELECTED: 'var(--project-color-border-selected)',
	COLOR_ELEVATION_SURFACE: 'var(--project-color-elevation-surface)',
	COLOR_ELEVATION_SURFACE_SUNKEN: 'var(--project-color-elevation-surface-sunken)',
	COLOR_ICON_DANGER: 'var(--project-color-icon-danger)',
	COLOR_ICON_INFORMATION: 'var(--project-color-icon-information)',
	COLOR_ICON_SUCCESS: 'var(--project-color-icon-success)',
	COLOR_ICON_WARNING: 'var(--project-color-icon-warning)',
	COLOR_LINK: 'var(--project-color-link)',
	COLOR_LINK_PRESSED: 'var(--project-color-link-pressed)',
	COLOR_SKELETON: 'var(--project-skeleton)',
	COLOR_TEXT: 'var(--project-color-text)',
	COLOR_TEXT_ACCENT_SELECTED: 'var(--project-text-accent-selected)',
	COLOR_TEXT_DISABLED: 'var(--project-color-text-disabled)',
	COLOR_TEXT_INVERSE: 'var(--project-color-text-inverse)',
	COLOR_TEXT_SELECTED: 'var(--project-color-text-selected)',
	COLOR_TEXT_SUBTLE: 'var(--project-color-text-subtle)',
	COLOR_TEXT_SUBTLEST: 'var(--project-color-text-subtlest)',
	CONTENT_HEIGHT: 'var(--project-content-height)',
	CONTENT_WIDTH: 'var(--project-content-width)',
	ELEVATION_SURFACE: 'var(--project-elevation-surface)',
	ELEVATION_SURFACE_BLEND_MODE: 'var(--project-elevation-surface-blend-mode)',
	ELEVATION_SURFACE_CONTRAST: 'var(--project-elevation-surface-contrast)',
	ELEVATION_SURFACE_CONTRAST_BOLDER: 'var(--project-elevation-surface-contrast-bolder)',
	ELEVATION_SURFACE_SWIMLANE: 'var(--project-elevation-surface-swimlane)',
	WRAPPER_BACKGROUND: 'var(--project-wrapper-background)',
	WRAPPER_BORDER_RADIUS: 'var(--project-wrapper-border-radius)',
	WRAPPER_HEIGHT: 'var(--project-wrapper-height)',
	WRAPPER_PADDING: 'var(--project-wrapper-padding)',
	WRAPPER_WIDTH: 'var(--project-wrapper-width)',
};

// IMPORTANT: If changing this file, make sure to update the ThemeTokensReset component in the project-theme-tokens-reset package.
export const BASE_THEME: ProjectThemeTokens = {
	'color.background.neutral.subtle.hovered.contrast': token(
		'color.background.neutral.subtle.hovered',
	),
	'color.background.accent.selected': '#8FB8F6', // B700
	'color.background.brand.bold': token('color.background.brand.bold'),
	'color.background.brand.bold.hovered': token('color.background.brand.bold.hovered'),
	'color.background.brand.bold.pressed': token('color.background.brand.bold.pressed'),
	'color.background.disabled': token('color.background.disabled'),
	'color.background.input': token('color.background.input'),
	'color.background.neutral': token('color.background.neutral'),
	'color.background.neutral.hovered': token('color.background.neutral.hovered'),
	'color.background.neutral.pressed': token('color.background.neutral.pressed'),
	'color.background.neutral.subtle.hovered': token('color.background.neutral.subtle.hovered'),
	'color.background.neutral.subtle.pressed': token('color.background.neutral.subtle.pressed'),
	'color.background.selected': token('color.background.selected'),
	'color.elevation.surface': token('elevation.surface'),
	'color.elevation.surface.sunken': token('elevation.surface.sunken'),
	'color.elevation.surface.swimlane': token('elevation.surface'),
	'color.border': token('color.border'),
	'color.border.input': token('color.border.input'),
	'color.border.inverse': token('color.border.inverse'),
	'color.border.selected': token('color.border.selected'),
	'color.icon.danger': token('color.icon.danger'),
	'color.icon.information': token('color.icon.information'),
	'color.icon.success': token('color.icon.success'),
	'color.icon.warning': token('color.icon.warning'),
	'color.link': token('color.link'),
	'color.link.pressed': token('color.link.pressed'),
	'color.skeleton': token('color.background.neutral'),
	'color.text': token('color.text'),
	'color.text.accent.selected': token('color.text'),
	'color.text.disabled': token('color.text.disabled'),
	'color.text.inverse': token('color.text.inverse'),
	'color.text.selected': token('color.text.selected'),
	'color.text.subtle': token('color.text.subtle'),
	'color.text.subtlest': token('color.text.subtlest'),
	'elevation.surface.contrast': 'transparent',
	'elevation.surface.contrast.bolder': 'transparent',
	'elevation.surface.blend.mode': 'darken',
};

export const HIGH_BRIGHTNESS_THEME: PartialProjectThemeTokens = {
	'color.background.accent.selected': token('color.background.neutral.hovered'),
	'color.background.brand.bold': token('color.background.accent.gray.bolder'),
	'color.background.brand.bold.hovered': token('color.background.accent.gray.bolder.hovered'),
	'color.background.brand.bold.pressed': token('color.background.accent.gray.bolder.pressed'),
	'color.background.disabled': token('color.background.neutral'),
	'color.background.input': 'rgba(255, 255, 255, 0.8)',
	'color.background.selected': token('color.background.accent.gray.bolder.pressed'),
	'color.text.selected': token('color.text.inverse'),
};

export const LOW_BRIGHTNESS_THEME: PartialProjectThemeTokens = {
	'color.background.neutral.subtle.hovered.contrast': 'rgba(255, 255, 255, 0.08)',
	'color.background.accent.selected': token('color.background.neutral.hovered'),
	'color.background.brand.bold': 'rgba(255, 255, 255, 0.8)',
	'color.background.brand.bold.hovered': 'rgba(255, 255, 255, 0.6)',
	'color.background.brand.bold.pressed': 'rgba(255, 255, 255, 0.4)',
	'color.background.disabled': 'rgba(255, 255, 255, 0.08)',
	'color.background.input': 'rgba(255, 255, 255, 0.08)',
	'color.background.neutral': 'rgba(255, 255, 255, 0.08)',
	'color.background.neutral.hovered': 'rgba(255, 255, 255, 0.16)',
	'color.background.neutral.pressed': 'rgba(255, 255, 255, 0.28)',
	'color.background.neutral.subtle.hovered': 'rgba(255, 255, 255, 0.08)',
	'color.background.neutral.subtle.pressed': 'rgba(255, 255, 255, 0.16)',
	'color.background.selected': 'rgba(255, 255, 255, 0.6)',
	'color.elevation.surface': '#172b4d',
	'color.border': 'rgba(255, 255, 255, 0.14)',
	'color.border.input': 'rgba(255, 255, 255, 0.3)',
	'color.border.inverse': 'rgba(255, 255, 255, 0.3)',
	'color.border.selected': '#FFFFFF', // N0
	'color.icon.danger': '#FFFFFF', // N0
	'color.icon.information': '#FFFFFF', // N0
	'color.icon.success': '#FFFFFF', // N0
	'color.icon.warning': '#FFFFFF', // N0
	'color.skeleton': 'rgba(255,255,255,0.08)',
	'color.text': '#FFFFFF', // N0
	'color.text.disabled': 'rgba(255, 255, 255, 0.6)',
	'color.text.inverse': '#172B4D', // Neutral 1000
	'color.text.selected': '#172B4D', // Neutral 1000
	'color.text.subtle': 'rgba(255, 255, 255, 0.8)',
	'color.text.subtlest': 'rgba(255, 255, 255, 0.8)',
};

export const GRADIENT_THEME: PartialProjectThemeTokens = {
	'color.link': '#CCE0FF', // Blue 200
	'color.link.pressed': '#E9F2FF', // Blue 100
};

// Image
export const IMAGE_BACKGROUND_CONFIG: ImageBackgroundConfig = {
	name: 'image',
	brightness: 'high',
	theme: {
		light: {
			'color.background.brand.bold': token('color.background.accent.gray.bolder'),
			'color.background.brand.bold.hovered': token('color.background.accent.gray.bolder.hovered'),
			'color.background.brand.bold.pressed': token('color.background.accent.gray.bolder.pressed'),
			'color.background.selected': token('color.background.accent.gray.bolder.pressed'),
			'color.link': token('color.text.accent.blue.bolder'),
			'color.link.pressed': token('color.text.accent.blue'),
			'color.text.selected': token('color.text.inverse'),
			'elevation.surface.contrast': 'rgba(255,255,255,0.82)',
			'elevation.surface.contrast.bolder': token('elevation.surface'),
		},
		dark: {
			'color.background.brand.bold': token('color.background.accent.gray.bolder'),
			'color.background.brand.bold.hovered': token('color.background.accent.gray.bolder.hovered'),
			'color.background.brand.bold.pressed': token('color.background.accent.gray.bolder.pressed'),
			'color.background.input': 'rgba(34, 39, 43, 0.8)',
			'color.background.selected': token('color.background.accent.gray.bolder.pressed'),
			'color.elevation.surface': '#172b4d',
			'color.border': 'rgba(255, 255, 255, 0.14)',
			'color.border.input': 'rgba(255, 255, 255, 0.3)',
			'color.border.inverse': 'rgba(255, 255, 255, 0.3)',
			'color.icon.danger': '#FFFFFF', // N0
			'color.icon.information': '#FFFFFF', // N0
			'color.icon.success': '#FFFFFF', // N0
			'color.icon.warning': '#FFFFFF', // N0
			'color.link': token('color.text.accent.blue.bolder'),
			'color.link.pressed': token('color.text.accent.blue'),
			'color.skeleton': 'rgba(255,255,255,0.8)',
			'color.text': '#FFFFFF', // N0
			'color.text.selected': token('color.text.inverse'),
			'elevation.surface.contrast': 'rgba(29, 33, 37, 0.82)',
			'elevation.surface.contrast.bolder': token('elevation.surface'),
		},
	},
};

export const NO_BACKGROUND_CONFIG: AdaptiveColorBackgroundConfig = {
	name: 'no-background',
	brightness: 'high',
	theme: {
		light: BASE_THEME,
		dark: {
			...BASE_THEME,
			'color.text.accent.selected': token('color.text.inverse'),
		},
	},
};

const ADAPTIVE_CONFIG_BASE: BaseThemeTokens = {
	'color.elevation.surface': token('elevation.surface.hovered'),
	'color.background.input': token('color.background.neutral'),
	'color.link': token('color.text'),
	'color.link.pressed': token('color.text'),
	'color.elevation.surface.sunken': token('elevation.surface.sunken'),
	'color.background.selected': token('color.background.neutral.hovered'),
	'color.background.neutral.subtle.hovered.contrast': token('elevation.surface.hovered'),
	'color.border.selected': token('color.border.bold'),
	'color.text.selected': token('color.text'),
	'color.text': token('color.text'),
	'color.background.brand.bold': token('color.background.neutral.bold'),
	'color.background.brand.bold.hovered': token('color.background.neutral.bold.hovered'),
	'color.background.brand.bold.pressed': token('color.background.neutral.bold.pressed'),
	'color.elevation.surface.swimlane': token('elevation.surface.hovered'),
};

export const createAdaptiveColorConfig = ({
	name,
	overrides,
}: {
	name: ColorThemeName;
	overrides: Partial<AdaptiveColorBackgroundConfig['theme']['light']>;
}): AdaptiveColorBackgroundConfig => {
	const adaptiveColorTheme = {
		...ADAPTIVE_CONFIG_BASE,
		...overrides,
	};

	return {
		name,
		brightness: 'high',
		theme: {
			light: adaptiveColorTheme,
			dark: adaptiveColorTheme,
		},
	};
};

export const createAdaptiveGradientConfig = ({
	name,
	overridesAllThemes,
	overridesLightTheme,
	overridesDarkTheme,
}: {
	name: GradientThemeName;
	overridesAllThemes: Partial<AdaptiveGradientBackgroundConfig['theme']['light']>;
	overridesLightTheme: Partial<AdaptiveGradientBackgroundConfig['theme']['light']>;
	overridesDarkTheme: Partial<AdaptiveGradientBackgroundConfig['theme']['dark']>;
}): AdaptiveGradientBackgroundConfig => {
	const adaptiveGradientTheme = {
		'elevation.surface': token('elevation.surface'),
		...ADAPTIVE_CONFIG_BASE,
		...overridesAllThemes,
	};

	return {
		name,
		brightness: 'high',
		theme: {
			light: {
				...adaptiveGradientTheme,
				...overridesLightTheme,
			},
			dark: { ...adaptiveGradientTheme, ...overridesDarkTheme },
		},
	};
};

export const ADAPTIVE_IMAGE_BACKGROUND_CONFIG: ImageBackgroundConfig = {
	name: 'image',
	brightness: 'high',
	theme: {
		light: {
			...ADAPTIVE_CONFIG_BASE,
			'elevation.surface.blend.mode': 'normal',
			'elevation.surface.contrast': 'rgba(255,255,255,0.82)',
			'elevation.surface.contrast.bolder': token('elevation.surface'),
		},
		dark: {
			...ADAPTIVE_CONFIG_BASE,
			'elevation.surface.blend.mode': 'darken',
			'elevation.surface.contrast': 'rgba(29, 33, 37, 0.82)',
			'elevation.surface.contrast.bolder': token('elevation.surface'),
		},
	},
};

export const ADAPTIVE_GRAY_BACKGROUND_CONFIG: AdaptiveColorBackgroundConfig =
	createAdaptiveColorConfig({
		name: 'adaptive-gray',
		overrides: {
			'color.elevation.surface.sunken': token('elevation.surface.pressed'),
			'color.elevation.surface.swimlane': token('color.background.accent.gray.subtlest'),
		},
	});
export const ADAPTIVE_PURPLE_BACKGROUND_CONFIG: AdaptiveColorBackgroundConfig =
	createAdaptiveColorConfig({
		name: 'adaptive-purple',
		overrides: {
			'color.elevation.surface': token('color.background.accent.purple.subtlest'),
			'color.link': token('color.text.accent.purple'),
			'color.link.pressed': token('color.text.accent.purple'),
			'color.elevation.surface.sunken': token('color.background.accent.purple.subtler'),
			'color.elevation.surface.swimlane': token('color.background.accent.purple.subtlest'),
		},
	});
export const ADAPTIVE_BLUE_BACKGROUND_CONFIG: AdaptiveColorBackgroundConfig =
	createAdaptiveColorConfig({
		name: 'adaptive-blue',
		overrides: {
			'color.elevation.surface': token('color.background.accent.blue.subtlest'),
			'color.link': token('color.text.accent.blue'),
			'color.link.pressed': token('color.text.accent.blue'),
			'color.elevation.surface.sunken': token('color.background.accent.blue.subtler'),
			'color.elevation.surface.swimlane': token('color.background.accent.blue.subtlest'),
		},
	});
export const ADAPTIVE_TEAL_BACKGROUND_CONFIG: AdaptiveColorBackgroundConfig =
	createAdaptiveColorConfig({
		name: 'adaptive-teal',
		overrides: {
			'color.elevation.surface': token('color.background.accent.teal.subtlest'),
			'color.link': token('color.text.accent.teal'),
			'color.link.pressed': token('color.text.accent.teal'),
			'color.elevation.surface.sunken': token('color.background.accent.teal.subtler'),
			'color.elevation.surface.swimlane': token('color.background.accent.teal.subtlest'),
		},
	});
export const ADAPTIVE_GREEN_BACKGROUND_CONFIG: AdaptiveColorBackgroundConfig =
	createAdaptiveColorConfig({
		name: 'adaptive-green',
		overrides: {
			'color.elevation.surface': token('color.background.accent.green.subtlest'),
			'color.link': token('color.text.accent.green'),
			'color.link.pressed': token('color.text.accent.green'),
			'color.elevation.surface.sunken': token('color.background.accent.green.subtler'),
			'color.elevation.surface.swimlane': token('color.background.accent.green.subtlest'),
		},
	});
export const ADAPTIVE_MAGENTA_BACKGROUND_CONFIG: AdaptiveColorBackgroundConfig =
	createAdaptiveColorConfig({
		name: 'adaptive-magenta',
		overrides: {
			'color.elevation.surface': token('color.background.accent.magenta.subtlest'),
			'color.link': token('color.text.accent.magenta'),
			'color.link.pressed': token('color.text.accent.magenta'),
			'color.elevation.surface.sunken': token('color.background.accent.magenta.subtler'),
			'color.elevation.surface.swimlane': token('color.background.accent.magenta.subtlest'),
		},
	});

// Gradients
const ADAPTIVE_GRADIENT_GRAY_META: AdaptiveGradientBackgroundConfig = createAdaptiveGradientConfig({
	name: 'adaptive-gradient-gray',
	overridesAllThemes: {},
	overridesLightTheme: {
		'elevation.surface': 'linear-gradient(135deg, #F8F8F8 0%, #B7B9BE 100%)',
	},
	overridesDarkTheme: {
		'elevation.surface': 'linear-gradient(135deg, #292A2E 0%, #6B6E76 100%)',
	},
});

const ADAPTIVE_GRADIENT_PURPLE_META: AdaptiveGradientBackgroundConfig =
	createAdaptiveGradientConfig({
		name: 'adaptive-gradient-purple',
		overridesAllThemes: {
			'color.link': token('color.text.accent.blue'),
			'color.link.pressed': token('color.text.accent.blue'),
			'color.elevation.surface': token('color.background.accent.blue.subtlest.hovered'),
			'color.elevation.surface.sunken': token('color.background.accent.blue.subtlest'),
			'color.elevation.surface.swimlane': token('color.background.accent.blue.subtler'),
		},
		overridesLightTheme: {
			'elevation.surface': 'linear-gradient(135deg, #CFE1FD 0%, #EED7FC 100%)',
		},
		overridesDarkTheme: {
			'elevation.surface': 'linear-gradient(135deg, #123263 0%, #48245D 100%)',
		},
	});

const ADAPTIVE_GRADIENT_BLUE_META: AdaptiveGradientBackgroundConfig = createAdaptiveGradientConfig({
	name: 'adaptive-gradient-blue',
	overridesAllThemes: {
		'color.link': token('color.text.accent.blue'),
		'color.link.pressed': token('color.text.accent.blue'),
		'color.elevation.surface': token('color.background.accent.blue.subtlest.hovered'),
		'color.elevation.surface.sunken': token('color.background.accent.blue.subtlest'),
		'color.elevation.surface.swimlane': token('color.background.accent.blue.subtler'),
	},
	overridesLightTheme: {
		'elevation.surface': 'linear-gradient(135deg, #E9F2FE 0%, #669DF1 100%)',
	},
	overridesDarkTheme: {
		'elevation.surface': 'linear-gradient(135deg, #1C2B42 0%, #1868DB 100%)',
	},
});

const ADAPTIVE_GRADIENT_TEAL_META: AdaptiveGradientBackgroundConfig = createAdaptiveGradientConfig({
	name: 'adaptive-gradient-teal',
	overridesAllThemes: {
		'color.link': token('color.text.accent.teal'),
		'color.link.pressed': token('color.text.accent.teal'),
		'color.elevation.surface': token('color.background.accent.teal.subtlest.hovered'),
		'color.elevation.surface.sunken': token('color.background.accent.teal.subtlest'),
		'color.elevation.surface.swimlane': token('color.background.accent.teal.subtler'),
	},
	overridesLightTheme: {
		'elevation.surface': 'linear-gradient(135deg, #E7F9FF 0%, #6CC3E0 100%)',
	},
	overridesDarkTheme: {
		'elevation.surface': 'linear-gradient(135deg, #1E3137 0%, #227D9B 100%)',
	},
});

const ADAPTIVE_GRADIENT_GREEN_META: AdaptiveGradientBackgroundConfig = createAdaptiveGradientConfig(
	{
		name: 'adaptive-gradient-green',
		overridesAllThemes: {
			'color.link': token('color.text.accent.green'),
			'color.link.pressed': token('color.text.accent.green'),
			'color.elevation.surface': token('color.background.accent.green.subtlest.hovered'),
			'color.elevation.surface.sunken': token('color.background.accent.green.subtlest'),
			'color.elevation.surface.swimlane': token('color.background.accent.green.subtler'),
		},
		overridesLightTheme: {
			'elevation.surface': 'linear-gradient(135deg, #DCFFF1 0%, #4BCE97 100%)',
		},
		overridesDarkTheme: {
			'elevation.surface': 'linear-gradient(135deg, #1C3329 0%, #1F845A 100%)',
		},
	},
);

const ADAPTIVE_GRADIENT_MAGENTA_META: AdaptiveGradientBackgroundConfig =
	createAdaptiveGradientConfig({
		name: 'adaptive-gradient-magenta',
		overridesAllThemes: {
			'color.link': token('color.text.accent.magenta'),
			'color.link.pressed': token('color.text.accent.magenta'),
			'color.elevation.surface': token('color.background.accent.magenta.subtlest.hovered'),
			'color.elevation.surface.sunken': token('color.background.accent.magenta.subtlest'),
			'color.elevation.surface.swimlane': token('color.background.accent.magenta.subtler'),
		},
		overridesLightTheme: {
			'elevation.surface': 'linear-gradient(135deg, #FDD0EC 28.5%, #FD9891 100%)',
		},
		overridesDarkTheme: {
			'elevation.surface': 'linear-gradient(135deg, #50253F 28.5%, #AE2E24 100%)',
		},
	});

// Solid background colors available in the adaptive version of the background palette
// (This palette has a smaller selection of gradients/colors, and solid colors adapt to dark/light mode)
export const ADAPTIVE_COLOR_BACKGROUND_CONFIGS: AdaptiveColorBackgroundConfig[] = [
	ADAPTIVE_GRAY_BACKGROUND_CONFIG,
	ADAPTIVE_PURPLE_BACKGROUND_CONFIG,
	ADAPTIVE_BLUE_BACKGROUND_CONFIG,
	ADAPTIVE_TEAL_BACKGROUND_CONFIG,
	ADAPTIVE_GREEN_BACKGROUND_CONFIG,
	ADAPTIVE_MAGENTA_BACKGROUND_CONFIG,
];

// Gradients available in the adaptive version of the background palette
// (This palette has a smaller selection of gradients/colors, and solid colors adapt to dark/light mode)
export const ADAPTIVE_GRADIENT_BACKGROUND_CONFIGS: AdaptiveGradientBackgroundConfig[] = [
	ADAPTIVE_GRADIENT_GRAY_META,
	ADAPTIVE_GRADIENT_PURPLE_META,
	ADAPTIVE_GRADIENT_BLUE_META,
	ADAPTIVE_GRADIENT_TEAL_META,
	ADAPTIVE_GRADIENT_GREEN_META,
	ADAPTIVE_GRADIENT_MAGENTA_META,
];

export const DEFAULT_BACKGROUND_CONFIG: AdaptiveColorBackgroundConfig = NO_BACKGROUND_CONFIG;

export const BUSINESS_WRAPPER_PADDING = 24;

export const HORIZONTAL_NAV_HEIGHT = 104;
export const BUSINESS_NAV_HEIGHT: '104px' = `${HORIZONTAL_NAV_HEIGHT}px`;
export const BUSINESS_WRAPPER_HEIGHT: string = `calc(100vh - ${PAGE_LAYOUT_OFFSET_TOP} - ${BUSINESS_NAV_HEIGHT})`;
export const BUSINESS_WRAPPER_WITH_BACKGROUND_HEIGHT: string = `calc(100vh - ${PAGE_LAYOUT_OFFSET_TOP} - ${BUSINESS_NAV_HEIGHT} - ${
	BUSINESS_WRAPPER_PADDING * 2
}px)`;

export const BUSINESS_WRAPPER_WIDTH: string = `calc(100vw - ${UNSAFE_DO_NOT_USE_LEFT_PANEL_WIDTH} - ${UNSAFE_DO_NOT_USE_LEFT_SIDEBAR_WIDTH} - ${UNSAFE_DO_NOT_USE_RIGHT_PANEL_WIDTH} - ${UNSAFE_DO_NOT_USE_RIGHT_SIDEBAR_WIDTH}) !important`;
export const BUSINESS_WRAPPER_WITH_BACKGROUND_WIDTH: string = `calc(100vw - ${UNSAFE_DO_NOT_USE_LEFT_PANEL_WIDTH} - ${UNSAFE_DO_NOT_USE_LEFT_SIDEBAR_WIDTH} - ${UNSAFE_DO_NOT_USE_RIGHT_PANEL_WIDTH} - ${UNSAFE_DO_NOT_USE_RIGHT_SIDEBAR_WIDTH} - ${
	BUSINESS_WRAPPER_PADDING * 2
}px) !important`;

// Maybe won't need this
export const ELEVATION_SURFACE_CONTRAST_OPACITY = 82;
